import { Button } from '@/components/ui/button'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import Card from 'app/frontend/base/components/Card'
import { dayOptions } from 'app/frontend/base/components/form-fields/CronField'
import DateField from 'app/frontend/base/components/form-fields/DateField'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useInstances } from 'app/frontend/base/config/queries'
import AddEventDialog, {
  AddEventFormValues,
} from 'app/frontend/base/pages/TeamDashboard/subpages/TeamEventOverview/components/AddEventDialog'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import { raidSizeLabel } from 'app/frontend/base/helpers/raidSize'
import EventParticipantStatus from 'app/frontend/types/custom/EventParticipantStatus'
import { getEventStatusColorClass } from 'app/frontend/base/components/EventStatusIcon'
import { useDifficulties } from 'app/frontend/base/hooks/useDifficulties'
import RaidsPlanningScope from 'app/frontend/types/custom/RaidsPlanningScope'
import { format } from 'date-fns'
import { AlertTriangle, Info, Trash2, Undo2 } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export interface EditableRecurringEvent
  extends Partial<
    Omit<
      wowaudit.RecurringEvent,
      | 'instanceId'
      | 'lockHoursBeforeStart'
      | 'lockAtPresentCount'
      | 'defaultSignupStatus'
      | 'defaultSelectionStatus'
      | 'defaultRaidsPlanningScope'
      | 'guestPolicy'
    >
  > {
  instanceId?: string | number | null
  lockHoursBeforeStart?: number | null
  lockAtPresentCount?: number | null
  defaultSignupStatus?: EventParticipantStatus | null
  defaultSelectionStatus?: string | null
  defaultRaidsPlanningScope?: RaidsPlanningScope | null
  guestPolicy?: string | null
  _destroy?: boolean
}

const name = 'recurringEventsAttributes'

const EventScheduleFormContent = () => {
  const { t } = useTranslation()
  const { team } = useTeam()
  const { data: instances } = useInstances()
  const { defaultDifficulty } = useDifficulties()
  const { watch, setValue } = useFormContext()
  const { append, remove } = useFieldArray({ name })
  const [editingIndex, setEditingIndex] = useState<number | null>(null)
  const recurringEvents: EditableRecurringEvent[] = watch(name) ?? []
  const today = format(new Date(), 'yyyy-MM-dd')

  const originalRepeatUntilById = useMemo(() => {
    return (team.recurringEvents ?? []).reduce<Record<number, string | null>>(
      (acc, event) => {
        if (event.id === undefined || event.id === null) return acc
        acc[event.id] = event.repeatUntil
          ? format(new Date(event.repeatUntil), 'yyyy-MM-dd')
          : null
        return acc
      },
      {},
    )
  }, [team.recurringEvents])
  const originalWeekDayById = useMemo(() => {
    return (team.recurringEvents ?? []).reduce<Record<number, string | null>>(
      (acc, event) => {
        if (event.id === undefined || event.id === null) return acc
        acc[event.id] =
          event.weekDay !== undefined && event.weekDay !== null
            ? `${event.weekDay}`
            : null
        return acc
      },
      {},
    )
  }, [team.recurringEvents])

  const hasRepeatUntilWarning = (event: EditableRecurringEvent) => {
    const originalRepeatUntil =
      event.id !== undefined && event.id !== null
        ? originalRepeatUntilById[event.id] ?? null
        : null
    if (!event.repeatUntil) return false
    if (!originalRepeatUntil) return true
    return event.repeatUntil < originalRepeatUntil
  }
  const hasWeekDayWarning = (event: EditableRecurringEvent) => {
    if (event.id === undefined || event.id === null) return false
    const originalWeekDay = originalWeekDayById[event.id] ?? null
    if (originalWeekDay === null) return false
    return `${event.weekDay}` !== originalWeekDay
  }

  const getNextRecurringEventDefaults = (): EditableRecurringEvent => {
    const previousEvent = recurringEvents[recurringEvents.length - 1]
    return {
      weekDay: `${previousEvent?.weekDay ?? '1'}`,
      repeatFrom: today,
      repeatUntil: undefined,
      startTime: previousEvent?.startTime ?? '20:00',
      finishTime: previousEvent?.finishTime ?? '23:00',
      optional: previousEvent?.optional ?? false,
      activityType: previousEvent?.activityType ?? 'raids',
      instanceId: previousEvent?.instanceId ?? null,
      difficulty: previousEvent?.difficulty ?? defaultDifficulty,
      raidSize: previousEvent?.raidSize ?? null,
      autoAddParticipants: previousEvent?.autoAddParticipants ?? true,
      lockHoursBeforeStart:
        previousEvent?.lockHoursBeforeStart ??
        team.settings.raidsAutoLock.value ??
        null,
      lockAtPresentCount: previousEvent?.lockAtPresentCount ?? null,
      defaultSignupStatus: previousEvent?.defaultSignupStatus ?? null,
      defaultSelectionStatus: previousEvent?.defaultSelectionStatus ?? null,
      defaultRaidsPlanningScope:
        previousEvent?.defaultRaidsPlanningScope ?? null,
      guestPolicy: previousEvent?.guestPolicy ?? null,
    }
  }

  return (
    <div className='mb-8'>
      <h5>{t('settings.eventSchedule.title')}</h5>
      <p className='mt-2 mb-4'>{t('settings.eventSchedule.description')}</p>

      <div className='flex flex-col gap-3'>
        {recurringEvents.map((event, index) => {
          const instanceName = event.instanceId
            ? instances?.find(
                (instance) => `${instance.id}` === `${event.instanceId}`,
              )?.name
            : null
          const isExistingEvent = event.id !== undefined && event.id !== null
          const isPastRepeatFrom =
            !!event.repeatFrom && event.repeatFrom < today
          const disableRepeatFrom = isExistingEvent && isPastRepeatFrom
          const repeatUntilMinDateString =
            event.repeatFrom && event.repeatFrom > today
              ? event.repeatFrom
              : today

          return (
            <Card key={`${event.id ?? 'new'}-${index}`} className='space-y-3'>
              <div className='grid grid-cols-1 lg:grid-cols-3 gap-3'>
                <SelectField
                  name={`${name}.${index}.weekDay`}
                  options={dayOptions}
                  transformOptions={false}
                  layout='stacked'
                  customLabel={t('settings.eventSchedule.weekDay')}
                />
                <DateField
                  name={`${name}.${index}.repeatFrom`}
                  layout='stacked'
                  customLabel={t('settings.eventSchedule.repeatFrom')}
                  minDate={new Date()}
                  disabled={disableRepeatFrom}
                />
                <DateField
                  name={`${name}.${index}.repeatUntil`}
                  layout='stacked'
                  customLabel={t('settings.eventSchedule.repeatUntil')}
                  customPlaceholder='settings.eventSchedule.repeatUntilPlaceholder'
                  minDate={new Date(repeatUntilMinDateString)}
                  isClearable
                />
              </div>

              <div className='flex items-start justify-between gap-3 border-t pt-3'>
                <div className='text-sm flex flex-wrap items-start gap-x-8 gap-y-2'>
                  <span className='inline-flex flex-col gap-0.5'>
                    <span className='text-muted-foreground text-xs'>Time</span>
                    <span>
                      {event.startTime} - {event.finishTime}
                    </span>
                  </span>
                  <span className='inline-flex flex-col gap-0.5'>
                    <span className='text-muted-foreground text-xs'>
                      Activity
                    </span>
                    <span>
                      {t(
                        `dashboard.team.events.activity-types.${
                          event.activityType ?? 'raids'
                        }`,
                      )}
                      {event.activityType === 'raids'
                        ? ` - ${instanceName ?? 'Current season'}`
                        : ''}
                    </span>
                  </span>
                  {event.activityType === 'raids' ? (
                    <span className='inline-flex flex-col gap-0.5'>
                      <span className='text-muted-foreground text-xs'>
                        Difficulty
                      </span>
                      <span>
                        {
                          difficultiesLabels[
                            event.difficulty ?? defaultDifficulty
                          ]
                        }
                      </span>
                    </span>
                  ) : null}
                  {event.activityType === 'raids' && event.raidSize ? (
                    <span className='inline-flex flex-col gap-0.5'>
                      <span className='text-muted-foreground text-xs'>
                        Raid size
                      </span>
                      <span>{raidSizeLabel(Number(event.raidSize))}</span>
                    </span>
                  ) : null}
                  {event.activityType === 'custom' ? (
                    <span className='inline-flex flex-col gap-0.5'>
                      <span className='text-muted-foreground text-xs'>
                        Title
                      </span>
                      <span>{event.title ?? ''}</span>
                    </span>
                  ) : null}
                  {event.optional ? (
                    <span className='inline-flex flex-col gap-0.5 min-w-20'>
                      <span className='text-muted-foreground text-xs'>
                        Type
                      </span>
                      <span>{t('settings.eventSchedule.optional')}</span>
                    </span>
                  ) : null}
                  {event.defaultSignupStatus ? (
                    <span className='inline-flex flex-col gap-0.5'>
                      <span className='text-muted-foreground text-xs'>
                        Default signup status
                      </span>
                      <span className='inline-flex items-center gap-1'>
                        <span
                          className={getEventStatusColorClass(
                            event.defaultSignupStatus,
                          )}
                        >
                          {t(
                            `settings.raidsAvailableStatuses.options.${event.defaultSignupStatus}`,
                          )}
                        </span>
                        <Tooltip>
                          <TooltipTrigger>
                            <Info size={14} className='opacity-70' />
                          </TooltipTrigger>
                          <TooltipContent className='max-w-64'>
                            This takes precedence over the defaults specified on
                            team rank and team level
                          </TooltipContent>
                        </Tooltip>
                      </span>
                    </span>
                  ) : null}
                </div>

                <div className='flex items-center gap-2'>
                  <Button
                    type='button'
                    size='sm'
                    variant='outline'
                    onClick={() => setEditingIndex(index)}
                  >
                    {t('settings.eventSchedule.configure')}
                  </Button>
                  <Button
                    type='button'
                    size='sm'
                    variant={event._destroy ? 'secondary' : 'destructive'}
                    onClick={() =>
                      event.id
                        ? setValue(`${name}.${index}._destroy`, !event._destroy)
                        : remove(index)
                    }
                  >
                    {event._destroy ? (
                      <Undo2 size={14} />
                    ) : (
                      <Trash2 size={14} />
                    )}
                  </Button>
                </div>
              </div>

              {event._destroy ? (
                <Alert variant='warning'>
                  <AlertTriangle className='h-4 w-4' />
                  <AlertTitle>
                    {t('settings.eventSchedule.deleteWarningTitle')}
                  </AlertTitle>
                  <AlertDescription>
                    {t('settings.eventSchedule.deleteWarningDescription')}
                  </AlertDescription>
                </Alert>
              ) : null}

              {hasRepeatUntilWarning(event) ? (
                <Alert variant='warning'>
                  <AlertTriangle className='h-4 w-4' />
                  <AlertTitle>
                    {t('settings.eventSchedule.repeatUntilWarningTitle')}
                  </AlertTitle>
                  <AlertDescription>
                    {t('settings.eventSchedule.repeatUntilWarningDescription')}
                  </AlertDescription>
                </Alert>
              ) : null}

              {hasWeekDayWarning(event) ? (
                <Alert variant='warning'>
                  <AlertTriangle className='h-4 w-4' />
                  <AlertTitle>
                    {t('settings.eventSchedule.weekDayWarningTitle')}
                  </AlertTitle>
                  <AlertDescription>
                    {t('settings.eventSchedule.weekDayWarningDescription')}
                  </AlertDescription>
                </Alert>
              ) : null}
            </Card>
          )
        })}

        <div className='flex justify-end'>
          <Button
            type='button'
            size='sm'
            variant='outline'
            onClick={() => append(getNextRecurringEventDefaults())}
          >
            {t('settings.eventSchedule.add')}
          </Button>
        </div>
      </div>

      <AddEventDialog
        open={editingIndex !== null}
        setOpen={(open) => {
          if (!open) setEditingIndex(null)
        }}
        hideDateField={true}
        showDefaultSetupMode={true}
        hideAutoAddParticipants={
          editingIndex !== null &&
          recurringEvents[editingIndex]?.id !== undefined &&
          recurringEvents[editingIndex]?.id !== null
        }
        title={t('settings.eventSchedule.configureDialogTitle')}
        submitLabel={t('shared.continue')}
        defaultValues={
          editingIndex === null
            ? undefined
            : (recurringEvents[editingIndex] as Partial<AddEventFormValues>)
        }
        onSubmit={(values) => {
          if (editingIndex === null) return
          setValue(`${name}.${editingIndex}.startTime`, values.startTime, {
            shouldDirty: true,
          })
          setValue(`${name}.${editingIndex}.finishTime`, values.finishTime, {
            shouldDirty: true,
          })
          setValue(`${name}.${editingIndex}.optional`, values.optional, {
            shouldDirty: true,
          })
          setValue(
            `${name}.${editingIndex}.activityType`,
            values.activityType,
            {
              shouldDirty: true,
            },
          )
          setValue(`${name}.${editingIndex}.instanceId`, values.instanceId, {
            shouldDirty: true,
          })
          setValue(`${name}.${editingIndex}.difficulty`, values.difficulty, {
            shouldDirty: true,
          })
          setValue(
            `${name}.${editingIndex}.raidSize`,
            values.raidSize != null ? Number(values.raidSize) : null,
            {
              shouldDirty: true,
            },
          )
          setValue(
            `${name}.${editingIndex}.autoAddParticipants`,
            values.autoAddParticipants,
            { shouldDirty: true },
          )
          setValue(
            `${name}.${editingIndex}.lockHoursBeforeStart`,
            values.lockHoursBeforeStart,
            { shouldDirty: true },
          )
          setValue(
            `${name}.${editingIndex}.lockAtPresentCount`,
            values.lockAtPresentCount,
            { shouldDirty: true },
          )
          setValue(
            `${name}.${editingIndex}.defaultSignupStatus`,
            values.defaultSignupStatus,
            { shouldDirty: true },
          )
          setValue(
            `${name}.${editingIndex}.defaultSelectionStatus`,
            values.defaultSelectionStatus,
            { shouldDirty: true },
          )
          setValue(
            `${name}.${editingIndex}.defaultRaidsPlanningScope`,
            values.defaultRaidsPlanningScope,
            { shouldDirty: true },
          )
          setValue(`${name}.${editingIndex}.guestPolicy`, values.guestPolicy, {
            shouldDirty: true,
          })
          setValue(`${name}.${editingIndex}.title`, values.title, {
            shouldDirty: true,
          })
        }}
      />
    </div>
  )
}

export default EventScheduleFormContent
